/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { ReactNode } from 'react'

import Button from '../Button'
import EmptyState from '../EmptyState'

const RETRY_LABEL = 'Retry'

interface LoadErrorStateProps {
  /** Short label — rendered in Title Case via the shared helper. */
  title: string
  /** Optional supporting sentence below the title. */
  subtitle?: string
  onRetry: () => void
  /** Spinner on Retry, and presses are ignored. */
  loading?: boolean
  /** Icon above the title. Defaults to a muted error mark. */
  icon?: ReactNode
  /**
   * Minimum height of the centered block. Pass the same floor the screen's
   * empty state uses so a failure sits where "nothing here yet" would.
   */
  minHeight?: string
}

/**
 * Centered failure for a request that did not load: the shared empty state
 * plus a Retry button. Use this wherever an API call fails and the screen
 * has nothing else to show.
 */
const LoadErrorState = ({
  title,
  subtitle,
  onRetry,
  loading = false,
  icon = <ExclamationCircleOutlined />,
  minHeight,
}: LoadErrorStateProps) => {
  'use memo'

  return (
    <EmptyState
      icon={icon}
      title={title}
      subtitle={subtitle}
      minHeight={minHeight}
      action={
        <Button
          text={capitalizeWords(RETRY_LABEL)}
          onClick={onRetry}
          variant='primary'
          isLoading={loading}
          style={retryStyle}
        />
      }
    />
  )
}

// The shared button grows on the cross axis of a column. Hold it to its
// label, at the same width as the mobile retry.
const retryStyle = css`
  flex: 0 0 auto;
  margin-top: 16px;
  min-width: 208px;
  padding: 8px 32px;
`

export default LoadErrorState
